import { useState, useCallback } from 'react';
import { Button, Badge } from '@tkpod/ui';
import type { OptionValueDto, MetadataFieldDef } from '@tkpod/shared';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DynamicValuesTableProps {
  values: OptionValueDto[];
  metadataSchema: MetadataFieldDef[];
  onEdit: (v: OptionValueDto) => void;
  onDelete: (v: OptionValueDto) => void;
  onReorder?: (orderedIds: string[]) => void;
}

export function DynamicValuesTable({ values, metadataSchema, onEdit, onDelete, onReorder }: DynamicValuesTableProps) {
  const tableFields = (metadataSchema ?? []).filter((f) => f.key && f.type && f.showInTable);

  const filterableSelectField = tableFields.find((f) => f.type === 'select' && f.options && f.options.length > 0);
  const [filterKey, setFilterKey] = useState('');
  const [filterValue, setFilterValue] = useState('');

  const isFiltering = !!(filterValue && filterableSelectField);
  const displayValues = isFiltering
    ? values.filter((v) => {
        const meta = v.metadata as Record<string, unknown>;
        return getNestedValue(meta, filterableSelectField.key) === filterValue;
      })
    : values;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorder) return;

      const oldIndex = values.findIndex((v) => v.id === active.id);
      const newIndex = values.findIndex((v) => v.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(values, oldIndex, newIndex);
      onReorder(reordered.map((v) => v.id));
    },
    [values, onReorder],
  );

  const colCount = 3 + tableFields.length + (onReorder ? 1 : 0);

  return (
    <div className="mt-4">
      {filterableSelectField && (filterableSelectField.options?.length ?? 0) > 0 && (
        <div className="flex items-center gap-2 px-6 pb-3">
          <label className="text-sm font-medium text-text-secondary">
            Filter by {filterableSelectField.label || filterableSelectField.key}:
          </label>
          <select
            value={filterKey === filterableSelectField.key ? filterValue : ''}
            onChange={(e) => {
              setFilterKey(filterableSelectField.key);
              setFilterValue(e.target.value);
            }}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All ({values.length})</option>
            {(filterableSelectField.options ?? []).map((opt) => {
              const count = values.filter((v) =>
                getNestedValue(v.metadata as Record<string, unknown>, filterableSelectField.key) === opt,
              ).length;
              return (
                <option key={opt} value={opt}>
                  {opt} ({count})
                </option>
              );
            })}
          </select>
          {filterValue && (
            <button
              type="button"
              onClick={() => { setFilterValue(''); setFilterKey(''); }}
              className="text-xs text-brand-600 hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={displayValues.map((v) => v.id)}
            strategy={verticalListSortingStrategy}
            disabled={isFiltering}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-secondary">
                  {onReorder && <th className="w-10 px-2 py-3" />}
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Display Name</th>
                  {tableFields.map((f) => (
                    <th key={f.key} className="px-6 py-3 font-medium">
                      {f.label || f.key}
                    </th>
                  ))}
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayValues.length === 0 ? (
                  <tr>
                    <td
                      colSpan={colCount}
                      className="px-6 py-8 text-center text-sm text-text-tertiary"
                    >
                      {filterValue ? 'No values match this filter.' : 'No values yet.'}
                    </td>
                  </tr>
                ) : (
                  displayValues.map((v) => (
                    <SortableRow
                      key={v.id}
                      value={v}
                      tableFields={tableFields}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      isDragDisabled={isFiltering || !onReorder}
                      showDragColumn={!!onReorder}
                    />
                  ))
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

function SortableRow({
  value,
  tableFields,
  onEdit,
  onDelete,
  isDragDisabled,
  showDragColumn,
}: {
  value: OptionValueDto;
  tableFields: MetadataFieldDef[];
  onEdit: (v: OptionValueDto) => void;
  onDelete: (v: OptionValueDto) => void;
  isDragDisabled: boolean;
  showDragColumn: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: value.id, disabled: isDragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const meta = value.metadata as Record<string, unknown>;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b border-border last:border-0 hover:bg-surface-secondary/50"
    >
      {showDragColumn && (
        <td className="w-10 px-2 py-3">
          {!isDragDisabled && (
            <button
              type="button"
              className="cursor-grab touch-none rounded p-1 text-text-tertiary hover:bg-surface-secondary hover:text-text-secondary active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="5.5" cy="3.5" r="1.5" />
                <circle cx="10.5" cy="3.5" r="1.5" />
                <circle cx="5.5" cy="8" r="1.5" />
                <circle cx="10.5" cy="8" r="1.5" />
                <circle cx="5.5" cy="12.5" r="1.5" />
                <circle cx="10.5" cy="12.5" r="1.5" />
              </svg>
            </button>
          )}
        </td>
      )}
      <td className="px-6 py-3 font-medium text-text-primary">{value.name}</td>
      <td className="px-6 py-3 text-text-secondary">{value.displayName}</td>
      {tableFields.map((f) => (
        <td key={f.key} className="px-6 py-3">
          <CellRenderer field={f} value={getNestedValue(meta, f.key)} />
        </td>
      ))}
      <td className="px-6 py-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(value)}>Edit</Button>
          <button
            type="button"
            onClick={() => onDelete(value)}
            className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function CellRenderer({ field, value }: { field: MetadataFieldDef; value: unknown }) {
  if (value == null || value === '') return <span className="text-text-tertiary">—</span>;

  switch (field.type) {
    case 'color':
      return (
        <div className="flex items-center gap-1.5">
          <div
            className="h-5 w-5 shrink-0 rounded border border-border"
            style={{ backgroundColor: String(value) }}
          />
          <span className="font-mono text-xs text-text-secondary">{String(value)}</span>
        </div>
      );

    case 'image':
      return (
        <img
          src={String(value)}
          alt={field.label}
          className="h-6 w-6 shrink-0 rounded border border-border object-cover"
        />
      );

    case 'number':
      return <span className="text-text-secondary">{String(value)}</span>;

    case 'select':
      return <Badge variant="default" size="sm">{String(value)}</Badge>;

    case 'boolean':
      return (
        <Badge variant={value ? 'success' : 'default'} size="sm">
          {value ? 'Yes' : 'No'}
        </Badge>
      );

    case 'string':
    default:
      return <span className="text-text-secondary">{String(value)}</span>;
  }
}

function getNestedValue(obj: Record<string, unknown>, key: string): unknown {
  const parts = key.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}
